// COPIA da TMA#718 di CriGio @ 8e60f982cc91900b4e051e8d7f642e628fc9cfe8: src/components/burraco/Carta.tsx — `INCHIOSTRO_2`, `INCHIOSTRO_4`, `FONDO_4`, `FONDO_2`, `OMBRA_CARTA` (righe 13-28), coi semi del CP (`c`, `d`, `h`, `s`) e senza la chiave `X`.
/**
 * La CARTA del modulo tavolo: cosa c'e scritto su una carta, per due mazzi, e le sue misure. Il disegno e `components/tavolo/Carta.tsx`.
 *
 * Due mazzi, una faccia ciascuno (ADR-0235: la Carta del kit regge anche la carta OFC — condizione di tma-orc, 18/10):
 *  - `francese` (52 carte, Chinese Poker / OFC): rango 2..14 (l'asso e 14, come nel protocollo del CP), semi `c` `d` `h` `s`;
 *  - `napoletano` (40 carte, scopa e scopone): grado 1..10 (asso, 2..7, fante 8, cavallo 9, re 10), semi denari, coppe, spade, bastoni.
 *    L'INDICE di una carta napoletana e quello di QScopa (`packages/regole/src/carte.ts`, QScopa@f1bede79): `seme * 10 + grado - 1`, semi nell'ordine
 *    denari, coppe, spade, bastoni. Qui non si importa QScopa: la formula e ripetuta, ed e la sola cosa del suo mazzo che serve per disegnare.
 *
 * I COLORI della faccia francese (`INCHIOSTRO_2`, `INCHIOSTRO_4`, `FONDO_4`, `FONDO_2`, `OMBRA_CARTA`) sono la copia della riga in cima, gli stessi valori; fino a questa
 * PR stavano in `components/chinesepoker/cartaCp.ts`, spostati qui senza cambiarli. Le chiavi dei semi sono
 * quelle del CP (`c`, `d`, `h`, `s`; nel burraco `C`, `D`, `H`, `S`); non c'e la chiave `X` (nessun jolly in OFC). Non sono token del tema: una carta deve restare
 * leggibile identica in ogni tema.
 * I COLORI della faccia napoletana sono nostri (il mazzo e disegnato da noi sul modello tradizionale, nessuna scansione: default del contratto `kit-e-scopa.md`, scelta di
 * prodotto di Luca): oro per denari e coppe, acciaio per spade, verde e legno per bastoni. Il fondo e lo stesso crema della francese (`FONDO_2`).
 */
export type SemeFrancese = 'c' | 'd' | 'h' | 's';
export type SemeNapoletano = 'denari' | 'coppe' | 'spade' | 'bastoni';

export interface FacciaFrancese {
  readonly mazzo: 'francese';
  /** 2..14, l'asso e 14. Il solo gioco che lo vuole basso (scala 40: A-2-3) passa 1: si disegna uguale, e `valoreRango` lo legge secondo l'opzione `asso`. */
  readonly rango: number;
  readonly seme: SemeFrancese;
}
/** Il jolly del mazzo da 108 (scala 40): `numero` dice quale dei due del mazzo (0 o 1), la COPIA e a parte (v. `idCarta`). */
export interface FacciaJolly {
  readonly mazzo: 'jolly';
  readonly numero: 0 | 1;
}
export interface FacciaNapoletana {
  readonly mazzo: 'napoletano';
  /** 1..10: asso, 2..7, fante (8), cavallo (9), re (10). */
  readonly grado: number;
  readonly seme: SemeNapoletano;
}
export type Faccia = FacciaFrancese | FacciaNapoletana | FacciaJolly;

/** Le misure di una carta (px): `rango` e `seme` sono i corpi del carattere dell'indice, `dx` il margine. Le taglie le sceglie il gioco (v. `MISURE_CP`). */
export interface MisureCarta {
  readonly w: number;
  readonly h: number;
  readonly rango: number;
  readonly seme: number;
  readonly raggio: number;
  readonly dx: number;
}

// ── francese ─────────────────────────────────────────────────────────────────
export const INCHIOSTRO_2: Record<SemeFrancese, string> = { s: '#1b2430', h: '#b3261e', d: '#b3261e', c: '#1b2430' };
export const INCHIOSTRO_4: Record<SemeFrancese, string> = { s: '#1b2430', h: '#b3261e', d: '#1557b0', c: '#2e7d32' };
export const FONDO_4: Record<SemeFrancese, [string, string]> = {
  s: ['#f1f3f7', '#dfe3ea'],
  h: ['#fdf0ec', '#f3dcd4'],
  d: ['#edf3fc', '#d9e5f6'],
  c: ['#eef6ec', '#dbe9d8'],
};
export const FONDO_2: [string, string] = ['#fbf7ec', '#ece2c9'];
export const sfumato = ([a, b]: [string, string]) => `linear-gradient(160deg, ${a} 0%, ${b} 100%)`;

/** Lo spessore e l'ombra: un filo di luce sul bordo alto, il bordo basso piu scuro, l'ombra sotto. */
export const OMBRA_CARTA = '0 0 0 1px rgba(60,45,20,.45), inset 0 1px 0 rgba(255,255,255,.75), inset 0 -2px 0 rgba(90,70,30,.18), 2px 3px 7px rgba(0,0,0,.38)';

export const GLIFO: Record<SemeFrancese, string> = { c: '♣', d: '♦', h: '♥', s: '♠' };
export const rango = (r: number): string => (r === 11 ? 'J' : r === 12 ? 'Q' : r === 13 ? 'K' : r === 14 || r === 1 ? 'A' : String(r));

/** Dove sta l'asso: `alto` (14, come nel protocollo del CP: il default) o `basso` (1, A-2-3 della scala 40). */
export type Asso = 'alto' | 'basso';
/** Il valore d'ordine di un rango: l'asso (14 o 1, comunque sia scritto) va dove dice `asso`, ogni altro rango resta com'e. Senza opzione, asso = 14. */
export const valoreRango = (r: number, asso: Asso = 'alto'): number => (r === 1 || r === 14 ? (asso === 'basso' ? 1 : 14) : r);

// ── jolly ────────────────────────────────────────────────────────────────────
export const GLIFO_JOLLY = '★';
/** Il viola dei jolly del burraco (`components/burraco/Carta.tsx`: `INCHIOSTRO_2.X`, `FONDO_2.X`), stessi valori: il jolly si riconosce uguale nei due giochi. */
export const INCHIOSTRO_JOLLY = '#6a1b9a';
export const FONDO_JOLLY: [string, string] = ['#f4f0ff', '#e3dcf6'];

// ── napoletano ───────────────────────────────────────────────────────────────
export const SEMI_NAPOLETANI: readonly SemeNapoletano[] = ['denari', 'coppe', 'spade', 'bastoni'];
export const CARTE_NAPOLETANE = 40;

/** La carta dall'indice di QScopa (0..39); fuori dall'intervallo, `null`. */
export function facciaNapoletana(indice: number): FacciaNapoletana | null {
  if (!Number.isInteger(indice) || indice < 0 || indice >= CARTE_NAPOLETANE) return null;
  return { mazzo: 'napoletano', seme: SEMI_NAPOLETANI[Math.floor(indice / 10)], grado: (indice % 10) + 1 };
}
export const indiceNapoletano = (f: FacciaNapoletana): number => SEMI_NAPOLETANI.indexOf(f.seme) * 10 + f.grado - 1;

/** Il mazzo intero, nell'ordine degli indici. */
export const MAZZO_NAPOLETANO: readonly FacciaNapoletana[] = Array.from({ length: CARTE_NAPOLETANE }, (_, i) => facciaNapoletana(i)!);

/** L'inchiostro dell'indice (scuro abbastanza da leggersi sul crema a 360 px) e i colori del seme disegnato: corpo, filo, dettaglio. */
export const COLORI_NAPOLETANI: Record<SemeNapoletano, { inchiostro: string; corpo: string; filo: string; dettaglio: string }> = {
  denari: { inchiostro: '#8a5a00', corpo: '#e0b23a', filo: '#7a4f00', dettaglio: '#b3261e' },
  coppe: { inchiostro: '#a3221b', corpo: '#c9301f', filo: '#6e1610', dettaglio: '#e0b23a' },
  spade: { inchiostro: '#1d4a8c', corpo: '#3a6fb8', filo: '#163a6e', dettaglio: '#e0b23a' },
  bastoni: { inchiostro: '#2b6a2e', corpo: '#8a5a2b', filo: '#4a2f12', dettaglio: '#3f8f3a' },
};

/** Le figure: fante, cavallo, re (gradi 8, 9, 10). L'indice scritto e il grado, uguale in ogni lingua: e anche il valore di presa. */
export const FIGURA: Record<number, 'fante' | 'cavallo' | 're'> = { 8: 'fante', 9: 'cavallo', 10: 're' };

/**
 * Dove stanno i semi di una carta da 1 a 7 (unita del disegno: un riquadro 60×80), e quanto sono grandi.
 * Disposizione tradizionale semplificata: 1 al centro; 2 e 3 in colonna; 4 ai quattro angoli; 5 col centro; 6 su due colonne; 7 come il 6 con uno in mezzo in alto.
 */
export function posizioniSemi(grado: number): { punti: readonly (readonly [number, number])[]; lato: number } {
  switch (grado) {
    case 1:
      return { punti: [[30, 40]], lato: 40 };
    case 2:
      return { punti: [[30, 20], [30, 60]], lato: 26 };
    case 3:
      return { punti: [[30, 13], [30, 40], [30, 67]], lato: 22 };
    case 4:
      return { punti: [[16, 20], [44, 20], [16, 60], [44, 60]], lato: 22 };
    case 5:
      return { punti: [[16, 16], [44, 16], [30, 40], [16, 64], [44, 64]], lato: 20 };
    case 6:
      return { punti: [[16, 13], [44, 13], [16, 40], [44, 40], [16, 67], [44, 67]], lato: 19 };
    case 7:
      return { punti: [[16, 11], [44, 11], [30, 27], [16, 45], [44, 45], [16, 69], [44, 69]], lato: 17 };
    default:
      return { punti: [], lato: 0 };
  }
}

/**
 * L'identita di una carta nel DOM (`data-carta`): nel CP `<r><s>` (com'era, la leggono gli effetti del CP e la usa come chiave), nel napoletano l'indice di QScopa, nel jolly `jolly<numero>`.
 * Con un mazzo doppio (scala 40) due carte uguali sono due carte: passando la COPIA (0 o 1) l'id diventa `<id>.<copia>`. Senza copia e la stringa di prima, carta per carta.
 */
export const idCarta = (f: Faccia, copia?: number): string => {
  const base = f.mazzo === 'francese' ? `${f.rango}${f.seme}` : f.mazzo === 'jolly' ? `jolly${f.numero}` : String(indiceNapoletano(f));
  return copia === undefined ? base : `${base}.${copia}`;
};

// ── scala 40 (doppio mazzo francese da 108, 4 jolly) ─────────────────────────
export const CARTE_SCALA40 = 108;
const CARTE_PER_MAZZO_SCALA40 = 54;
const SEMI_SCALA40: readonly SemeFrancese[] = ['c', 'd', 'h', 's'];

/**
 * La carta della scala 40 dall'indice 0..107 (QScala40 `packages/regole/src/carte.ts`: `mazzo * 54 + dentro`; `dentro` 0..51 = `seme * 13 + (rango - 1)` coi semi c, d, h, s e l'asso a rango 1;
 * 52 e 53 i jolly). Ritorna la faccia e la COPIA (0 o 1, il mazzo da cui viene), da passare a `Carta` e a `idCarta`; fuori da 0..107, `null`.
 * Qui l'asso e rango 1: l'ordine lo decide `valoreRango(rango, asso)`.
 */
export function facciaScala40(indice: number): { faccia: FacciaFrancese | FacciaJolly; copia: 0 | 1 } | null {
  if (!Number.isInteger(indice) || indice < 0 || indice >= CARTE_SCALA40) return null;
  const copia = Math.floor(indice / CARTE_PER_MAZZO_SCALA40) as 0 | 1;
  const dentro = indice % CARTE_PER_MAZZO_SCALA40;
  if (dentro >= 52) return { faccia: { mazzo: 'jolly', numero: (dentro - 52) as 0 | 1 }, copia };
  return { faccia: { mazzo: 'francese', seme: SEMI_SCALA40[Math.floor(dentro / 13)], rango: (dentro % 13) + 1 }, copia };
}
